/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */
import {
  Box,
  useTheme,
  alpha,
  InputAdornment,
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
  IconButton,
  Skeleton,
  Theme,
} from '@mui/material';
import {
  OptionLegGreeks,
  OptionType,
  PositionLeg,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import DeleteIcon from '@mui/icons-material/Delete';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { useRecoilValue } from 'recoil';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import debounce from 'lodash/debounce';
import { convertToDaysTillExpiry, getExpirationLabel } from 'util/iVol';
import { roundToCents } from 'util/optionsCalculator';
import { FixedSizeList as List } from 'react-window';
import {
  dayjs,
  formatAsCompactNumber,
  formatAsCurrency,
  formatAsPercentage,
} from 'util/shared';
import BuySellBtn from './BuySellBtn';
import { SGTooltip } from 'components/core';

// Column widths shared with the header row rendered by GroupedOptionLegs.
// SIDE | QTY | C/P | STRIKE | EXPIRY | PRICE | IV | DELTA | GAMMA | VEGA | THETA | (delete)
export const LEG_ROW_GRID_TEMPLATE =
  '60px 72px 56px 92px 112px 122px 74px 74px 74px 74px 74px 36px';

interface EditableOptionLegProps {
  leg: PositionLeg;
  expirationOptions: number[];
  disabledExpirationOptions: number[];
  strikeDeltaOptions: StrikeDeltaObject[];
  legGreeks: OptionLegGreeks | null;
  greeksLoading?: boolean;
  onEditLeg: (
    legId: number,
    updatedLeg: Partial<PositionLeg>,
    shouldUseDefaultPrice?: boolean,
  ) => void;
  onDeleteLeg: (legId: number) => void;
}

interface StrikeItemProps {
  index: number;
  style: React.CSSProperties;
  data: {
    strikeDeltaOptions: StrikeDeltaObject[];
    selectedStrike: number | null;
    onStrikeSelect: (strike: number) => void;
    isCall: boolean;
    isBuy: boolean;
  };
}

const compactSelectSx = (theme: Theme) => ({
  height: 32,
  fontSize: '12px',
  '& .MuiSelect-select': {
    padding: '6px 8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  svg: {
    color: alpha(theme.palette.primary.main, 0.5),
  },
});

const compactInputSx = (theme: Theme) => ({
  height: 32,
  fontSize: '12px',
  '& .MuiOutlinedInput-input': {
    padding: '6px 8px',
    fontSize: '12px',
    MozAppearance: 'textfield',
  },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& fieldset': {
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
  '&:hover fieldset': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused fieldset': {
    borderColor: theme.palette.primary.main,
  },
});

interface GreekValueProps {
  label: string;
  value: number;
  description: string;
  decimals?: number;
  format?: 'percent' | 'decimal';
  useColorCoding?: boolean;
  adjustedValue?: number;
}

const GreekValue = ({
  value,
  description,
  decimals = 3,
  format = 'decimal',
  useColorCoding = false,
  adjustedValue,
}: GreekValueProps) => {
  const theme = useTheme();
  const serverPosColor = useRecoilValue(positiveTrendColorState);
  const serverNegColor = useRecoilValue(negativeTrendColorState);

  const formatGreekValue = (val: number) => {
    if (format === 'percent') {
      return `${formatAsPercentage(val)}`;
    }
    return formatAsCompactNumber(val, { maximumFractionDigits: decimals });
  };

  const getGreekColor = (val: number) => {
    if (val > 0) {
      return serverPosColor;
    } else if (val < 0) {
      return serverNegColor;
    }
    return theme.palette.text.secondary;
  };

  const hasAdjustment =
    adjustedValue != null && Math.abs(adjustedValue - value) > 0.001;
  const displayValue = hasAdjustment ? adjustedValue : value;
  const color = hasAdjustment
    ? theme.palette.warning.main
    : useColorCoding
      ? getGreekColor(displayValue)
      : theme.palette.text.primary;

  return (
    <SGTooltip
      title={
        hasAdjustment
          ? `Base: ${formatGreekValue(value)} → Adjusted: ${formatGreekValue(adjustedValue)}`
          : description
      }
    >
      <Box
        sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}
      >
        {hasAdjustment && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: 6,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: theme.palette.warning.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PrecisionManufacturingIcon
              sx={{ fontSize: 8, color: theme.palette.warning.contrastText }}
            />
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{ fontSize: '12px', fontWeight: 600, color }}
        >
          {formatGreekValue(displayValue)}
        </Typography>
      </Box>
    </SGTooltip>
  );
};

const StrikeItem = ({ index, style, data }: StrikeItemProps) => {
  const { strikeDeltaOptions, selectedStrike, onStrikeSelect, isCall, isBuy } =
    data;
  const strikeDeltaObject = strikeDeltaOptions[index];
  const strike = strikeDeltaObject.strike;
  const delta = isCall
    ? strikeDeltaObject.callDelta
    : strikeDeltaObject.putDelta;
  const adjustedDelta = delta * (isBuy ? 1 : -1);
  return (
    <div key={strike} style={style}>
      <MenuItem
        value={strike || ''}
        onClick={() => onStrikeSelect(strike)}
        sx={{
          padding: '10px',
          width: '100%',
        }}
      >
        <Checkbox
          checked={selectedStrike === strike}
          style={{
            padding: '0px 10px 0px 0px',
          }}
        />
        <ListItemText
          primary={`${formatAsCurrency(strike)}`}
          secondary={`DELTA: ${formatAsCompactNumber(adjustedDelta, { maximumFractionDigits: 3 })}`}
          primaryTypographyProps={{
            fontSize: '13px',
          }}
          secondaryTypographyProps={{
            fontSize: '11px',
          }}
        />
      </MenuItem>
    </div>
  );
};

const EditableOptionLeg = ({
  leg,
  legGreeks,
  greeksLoading = false,
  onEditLeg,
  expirationOptions,
  disabledExpirationOptions,
  strikeDeltaOptions,
  onDeleteLeg,
}: EditableOptionLegProps) => {
  const theme = useTheme();
  const [localQuantity, setLocalQuantity] = useState<number | null>(
    Math.abs(leg.quantity),
  );
  const [localStrike, setLocalStrike] = useState<number>(leg.strike);
  const [localPrice, setLocalPrice] = useState<number | null>(
    roundToCents(leg.price_paid),
  );
  const [expirySelectorOpen, setExpirySelectorOpen] = useState(false);
  const [strikeSelectorOpen, setStrikeSelectorOpen] = useState(false);
  const [localExpiration, setLocalExpiration] = useState<number>(leg.expiry);
  const strikeListRef = useRef<List>(null);

  const strikeOptions = useMemo(() => {
    return strikeDeltaOptions.map((object) => object.strike);
  }, [strikeDeltaOptions]);

  // Update local state when props change
  useEffect(() => {
    setLocalPrice(roundToCents(leg.price_paid));
    setLocalStrike(leg.strike);
  }, [leg.price_paid, leg.strike]);

  // Debounced handlers
  const debouncedEditLeg = useCallback(
    debounce(
      (
        legId: number,
        updatedLeg: Partial<PositionLeg>,
        shouldUseDefaultPrice?: boolean,
      ) => {
        onEditLeg(legId, updatedLeg, shouldUseDefaultPrice);
      },
      300,
    ),
    [onEditLeg],
  );

  const handleTypeChange = (event: SelectChangeEvent<OptionType>) => {
    onEditLeg(
      leg.leg_id,
      { option_type: event.target.value as OptionType },
      true,
    );
  };

  const handleStrikeOpen = () => {
    setStrikeSelectorOpen(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (strikeListRef.current == null || localStrike == null) {
          return;
        }
        const selectedIndex = strikeOptions.indexOf(localStrike);
        if (selectedIndex !== -1) {
          strikeListRef.current.scrollToItem(selectedIndex, 'center');
        }
      });
    });
  };

  const handleStrikeClose = () => {
    setStrikeSelectorOpen(false);
  };

  const handleOpen = () => {
    setExpirySelectorOpen(true);
  };

  const handleClose = () => {
    setExpirySelectorOpen(false);
  };

  const handleExpirationChange = (event: SelectChangeEvent<number>) => {
    const newExpiration = Number(event.target.value);

    if (!newExpiration) {
      return;
    }

    const dateObj = dayjs(newExpiration);
    const today = dayjs();

    if (!dateObj.isValid() || dateObj.isBefore(today, 'day')) {
      return;
    }

    setLocalExpiration(newExpiration);
    debouncedEditLeg(leg.leg_id, { expiry: newExpiration }, true);
  };

  const handleStrikeSelect = (strike: number) => {
    setLocalStrike(strike);
    debouncedEditLeg(leg.leg_id, { strike }, true);
    setStrikeSelectorOpen(false);
  };

  const updatePrice = (newPrice: number) => {
    setLocalPrice(newPrice);
    debouncedEditLeg(leg.leg_id, { price_paid: newPrice });
  };

  const handleResetPrice = () => {
    if (legGreeks == null) {
      return;
    }
    const newPrice = Math.round(legGreeks.price * 100) / 100;
    updatePrice(newPrice);
  };

  const updateQuantity = (quantity: number) => {
    setLocalQuantity(Math.abs(quantity));
    debouncedEditLeg(leg.leg_id, { quantity }, true);
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalPrice(null);
    } else {
      const newPrice = parseFloat(value);
      if (!isNaN(newPrice)) {
        updatePrice(newPrice);
      }
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setLocalQuantity(null);
    } else {
      const newQuantity = parseInt(value);
      if (!isNaN(newQuantity)) {
        // Maintain sign of the leg quantity
        updateQuantity(newQuantity * (leg.quantity >= 0 ? 1 : -1));
      }
    }
  };

  const shouldRenderExpirationSelect = expirationOptions.length > 0;
  const shouldRenderStrikeSelect = strikeDeltaOptions.length > 0;

  // Calculate item height and list height for virtualization
  const ITEM_HEIGHT = 50; // Increased height to accommodate delta display
  const MAX_VISIBLE_ITEMS = 6;
  const listHeight = Math.min(
    strikeDeltaOptions.length * ITEM_HEIGHT,
    MAX_VISIBLE_ITEMS * ITEM_HEIGHT,
  );

  const strikeItemData = useMemo(
    () => ({
      strikeDeltaOptions: strikeDeltaOptions,
      selectedStrike: localStrike,
      isCall: leg.option_type === OptionType.CALL,
      isBuy: leg.quantity >= 0,
      onStrikeSelect: handleStrikeSelect,
    }),
    [
      strikeDeltaOptions,
      localStrike,
      handleStrikeSelect,
      leg.option_type,
      leg.quantity,
    ],
  );

  const isCall = leg.option_type === OptionType.CALL;
  const cpColor = isCall
    ? theme.palette.success.main
    : theme.palette.error.main;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: LEG_ROW_GRID_TEMPLATE,
        alignItems: 'center',
        columnGap: '12px',
        px: 2,
        py: 1.25,
        minWidth: 'fit-content',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <BuySellBtn
          onClick={() => updateQuantity(0 - leg.quantity)}
          isBuy={leg.quantity >= 0}
        />
      </Box>

      <OutlinedInput
        value={localQuantity ?? ''}
        onChange={handleQuantityChange}
        endAdornment={
          <InputAdornment position="end" sx={{ mr: 0 }}>
            <Typography
              sx={{ fontSize: '11px', color: theme.palette.text.secondary }}
            >
              x
            </Typography>
          </InputAdornment>
        }
        type="number"
        sx={{
          ...compactInputSx(theme),
          '& input': { textAlign: 'center', p: '6px 4px' },
        }}
      />

      <Select
        value={leg.option_type}
        onChange={handleTypeChange}
        MenuProps={{
          sx: { '& .MuiSelect-icon': { color: theme.palette.text.primary } },
        }}
        sx={{
          ...compactSelectSx(theme),
          color: cpColor,
          fontWeight: 700,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(cpColor, 0.4),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: cpColor,
          },
        }}
      >
        <MenuItem value={OptionType.CALL}>{OptionType.CALL}</MenuItem>
        <MenuItem value={OptionType.PUT}>{OptionType.PUT}</MenuItem>
      </Select>

      {shouldRenderStrikeSelect ? (
        <Select
          open={strikeSelectorOpen}
          onClose={handleStrikeClose}
          onOpen={handleStrikeOpen}
          value={localStrike || ''}
          sx={compactSelectSx(theme)}
          renderValue={(selected: number | string) => {
            if (selected === '' || selected == null) {
              return 'Strike';
            }
            return `${formatAsCurrency(Number(selected))}`;
          }}
          MenuProps={{
            sx: {
              zIndex: 10_001,
              '& .MuiSelect-icon': { color: theme.palette.text.primary },
            },
            PaperProps: {
              style: {
                width: '180px',
                maxHeight: `${listHeight + 16}px`,
                padding: 0,
              },
            },
          }}
        >
          <Box sx={{ height: listHeight, width: '100%' }}>
            <List
              ref={strikeListRef}
              height={listHeight}
              itemCount={strikeDeltaOptions.length}
              itemSize={ITEM_HEIGHT}
              itemData={strikeItemData}
              width="100%"
            >
              {StrikeItem}
            </List>
          </Box>
        </Select>
      ) : (
        <Box />
      )}

      {shouldRenderExpirationSelect ? (
        <Select
          open={expirySelectorOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          onChange={handleExpirationChange}
          value={localExpiration}
          sx={compactSelectSx(theme)}
          renderValue={(selected: number) =>
            `${dayjs(selected).format('YYYY-MM-DD')}`
          }
          MenuProps={{
            sx: {
              zIndex: 10_001,
              '& .MuiSelect-icon': { color: theme.palette.text.primary },
            },
            PaperProps: {
              style: { width: '160px', maxHeight: '250px' },
            },
          }}
        >
          {expirationOptions.map((utcTime: number) => (
            <MenuItem
              key={utcTime}
              value={utcTime}
              sx={{ padding: '10px' }}
              disabled={disabledExpirationOptions.includes(utcTime)}
            >
              <Checkbox
                checked={leg.expiry === utcTime}
                style={{ padding: '0px 10px 0px 0px' }}
              />
              <ListItemText
                primary={`${getExpirationLabel(utcTime.toString())}`}
                secondary={`${convertToDaysTillExpiry(
                  dayjs().valueOf(),
                  utcTime,
                )} DTE`}
                primaryTypographyProps={{ fontSize: '13px' }}
              />
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box />
      )}

      <OutlinedInput
        value={localPrice}
        onChange={handlePriceChange}
        type="number"
        inputProps={{ step: 0.01 }}
        startAdornment={
          <InputAdornment position="start">
            <Typography
              sx={{ fontSize: '12px', color: theme.palette.text.secondary }}
            >
              $
            </Typography>
          </InputAdornment>
        }
        endAdornment={
          legGreeks?.price && (
            <InputAdornment position="end">
              <IconButton
                data-ph-capture-attribute-element="opt-calc-leg-reset"
                size="small"
                onClick={handleResetPrice}
                sx={{ padding: '2px' }}
                color="primary"
              >
                <RestartAltIcon sx={{ fontSize: '14px' }} />
              </IconButton>
            </InputAdornment>
          )
        }
        sx={compactInputSx(theme)}
      />

      {greeksLoading ? (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={40}
              height={20}
              sx={{
                mx: 'auto',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          ))}
        </>
      ) : legGreeks ? (
        <>
          <GreekValue
            label="IV"
            description="Implied volatility (IV) measures the market's expectation of how much an asset's price will fluctuate in the future, calculated from option prices."
            value={legGreeks.iv}
            decimals={3}
            format="percent"
            adjustedValue={legGreeks.adjustedIv}
          />
          <GreekValue
            label="Delta (Δ)"
            description="First-order Greek which measures the sensitivity of an option's price to movement in the underlying security."
            value={legGreeks.delta * (leg.quantity >= 0 ? 1 : -1)}
            decimals={3}
            useColorCoding
          />
          <GreekValue
            label="Gamma (Γ)"
            description="Second-order Greek which dynamically shows the rate of change of delta (directional risk) compared to the rate of change in the underlying security."
            value={legGreeks.gamma * (leg.quantity >= 0 ? 1 : -1)}
            decimals={3}
            useColorCoding
          />
          <GreekValue
            label="Vega (𝜈)"
            description="First-order Greek which measures the sensitivity of an option's price to changes in implied volatility"
            value={legGreeks.vega * (leg.quantity >= 0 ? 1 : -1)}
            decimals={3}
            useColorCoding
          />
          <GreekValue
            label="Theta (Θ)"
            description="First-order Greek which measures how much an option's price changes relative to time."
            value={legGreeks.theta * (leg.quantity >= 0 ? 1 : -1)}
            decimals={3}
            useColorCoding
          />
        </>
      ) : (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                textAlign: 'center',
                color: theme.palette.text.disabled,
                fontSize: '12px',
              }}
            >
              —
            </Typography>
          ))}
        </>
      )}

      <IconButton
        data-ph-capture-attribute-element="opt-calc-leg-delete"
        size="small"
        color="error"
        onClick={() => onDeleteLeg(leg.leg_id)}
      >
        <DeleteIcon sx={{ fontSize: '16px' }} />
      </IconButton>
    </Box>
  );
};

export default EditableOptionLeg;
